import * as React from 'react';

import THandler from '../../../Shared/Types/THandler';
import {TFocusableNodeType} from '../../../Tree/Constants/FocusableNodeType';
import useFocus from '../Hooks/useFocus';
import UseFocusMutableState from '../Hooks/Util/UseFocusMutableState';

import IFocusableComponentInjectedProps from './Interfaces/IFocusableComponentInjectedProps';
import IFocusableComponentOptions from './Interfaces/IFocusableComponentOptions';
import TContainerPropsMap from './Types/TContainerPropsMap';
import withNullCheckRenderSpy from './Util/withNullCheckRenderSpy';

/**
 * A higher-order component for making any component focusable.
 *
 * Focusable components have a unique `focusableId` which associates them with a node in the
 * internal tree of focusable nodes, as well as a variety of injected props for binding focusable
 * events, and "actions" dictating focusable behaviour.
 *
 * @param FocusableComponent A reference to the userland component to be made focusable.
 * @param type An optional type dictating the behaviour of the focusable component.
 */

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const focusable = <TOriginalProps extends IFocusableComponentInjectedProps, T extends TFocusableNodeType>(
    FocusableComponent: React.ComponentType<TOriginalProps>,
    type?: T
) => {
    type TContainerOwnProps = TContainerPropsMap[T];

    // The returned focusable HOC's props are a combination of:
    // - any (optional/required) available props for a focusable leaf or container
    //   (TFocusableComponentOwnProps & TContainerProps)
    // - Any userland props defined for the `FocusableComponent` (TOriginalProps), which should be
    //   assumed to include props injected by the focusable HOC (IFocusableComponentInjectedProps)
    // - NB: injected props are omitted to ensure we do not force the consumer to provide them

    type TFocusableHocProps = IFocusableComponentOptions &
        TContainerOwnProps &
        Omit<TOriginalProps, keyof IFocusableComponentInjectedProps>;

    const componentDisplayName = `FocusableHoc<${FocusableComponent.displayName || FocusableComponent.name}>`;

    const FocusableHoc: React.FC<TFocusableHocProps> = props => {
        const {focusManager, isFocused, focusableId, setComponentWillRender, setFocusableElementRef, ...priv} =
            useFocus({
                ...props,
                componentDisplayName,
                type,
            });

        // Access private "mutable state" member

        const {__state} = priv as unknown as {
            __state: UseFocusMutableState<TFocusableNodeType>;
        };

        /**
         * A reference to the provided userland component, wrapped in the `withNullCheckRenderSpy`
         * HOC to be used instead of the component at render time.
         */

        const FocusableComponentWithNullCheck = React.useMemo(
            () =>
                withNullCheckRenderSpy<TOriginalProps>(
                    FocusableComponent,
                    setComponentWillRender
                ) as React.ComponentType<TOriginalProps>,
            []
        );

        // Props must be coerced back to `TOriginalProps` to ensure they can be passed back through
        // to the userland component

        const focusableComponentOriginalProps: TOriginalProps = props as any;

        // Ensure all props passed to `FocusableComponentWithNullCheck` never change so that the
        // component can be memoised effectively.

        const setUpHandler = React.useCallback((handler: THandler) => (__state.options.onUp = handler), []);
        const setDownHandler = React.useCallback((handler: THandler) => (__state.options.onDown = handler), []);
        const setLeftHandler = React.useCallback((handler: THandler) => (__state.options.onLeft = handler), []);
        const setRightHandler = React.useCallback((handler: THandler) => (__state.options.onRight = handler), []);
        const setBlurHandler = React.useCallback((handler: THandler) => (__state.options.onBlur = handler), []);
        const setFocusHandler = React.useCallback((handler: THandler) => (__state.options.onFocus = handler), []);
        const setSelectHandler = React.useCallback((handler: THandler) => (__state.options.onSelect = handler), []);

        const focusableComponentInjectedProps: IFocusableComponentInjectedProps = {
            focusManager,
            focusableId,
            isFocused,
            setUpHandler,
            setDownHandler,
            setLeftHandler,
            setRightHandler,
            setBlurHandler,
            setFocusHandler,
            setSelectHandler,
            setFocusableElementRef,
        };

        // Props passed to the child are a combination of:
        // - All props passed into the HOC from above (TOriginalProps)
        // - Props injected into the userland component by the focusable HOC
        //   (IFocusableComponentInjectedProps)

        const childProps: TOriginalProps & IFocusableComponentInjectedProps = {
            ...focusableComponentOriginalProps,
            ...focusableComponentInjectedProps,
        };

        return <FocusableComponentWithNullCheck {...childProps} />;
    };

    const MemoisedFocusableHoc = React.memo(FocusableHoc);

    MemoisedFocusableHoc.displayName = componentDisplayName;

    return MemoisedFocusableHoc;
};

export default focusable;
